'use client';
// Documents — catalogue documentaire filtrable, triable (design isList).
import React from 'react';
import { useGed } from '@/lib/store';
import { STATES, fmtDate, monEtatLecture, joursRestants, stateMeta } from '@/lib/meta';
import { BoutonCreer, TitrePage, StatePill, I, card, inputStyle, SkeletonTiles, LateBadge, Selecteur, ChampRecherche, PAR_PAGE } from '@/components/ui';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { EnteteEcran } from '@/components/EnteteEcran';
import { Norme } from '@/components/Norme';
import { AssistantDemande } from '@/components/AssistantDemande';
import { RelanceLot } from '@/components/RelanceLot';
import { filtreMoi, ETAPES_MOI } from '@/lib/mes-documents';
import { IconeApercu } from '@/components/ApercuAnime';

import { Bouton } from '@/shared/Bouton';
/**
 * ⚠️ DÉRIVÉE DE `STATES`, ET PLUS RECOPIÉE À LA MAIN (relevé DAT-7 du
 * 26/08/2026 : « ces deux documents sont infiltrables »).
 *
 * Cette table était une COPIE locale de sept états sur treize. Les trois états
 * des documents EXTERNES — « Enregistré », « Adopté », « Remplacé » — avaient
 * été ajoutés au vocabulaire sans être reportés ici : `DE-DOC-001` (Adopté) et
 * `DE-ORG-001` (Enregistré) s'affichaient dans le tableau, avec leur pastille,
 * mais aucune entrée du filtre ne permettait de les isoler.
 *
 * ⚠️ L'ASYMÉTRIE EST CE QUI COMPTE. Un filtre qui propose un état sans document
 * ne coûte rien : il ne rend aucune ligne, et c'est une réponse juste. Un filtre
 * qui OMET un état rend des documents inatteignables — et rien ne le signale,
 * puisqu'ils apparaissent tant qu'on ne filtre pas. Dériver de la source fait
 * pencher du bon côté par construction.
 */
const STATUT_LABEL: Record<string, string> = Object.fromEntries(
  Object.values(STATES).map((s) => [s.key, s.label]),
);
// Sept colonnes : le PROCESSUS et la DISCIPLINE sont deux axes indépendants et
// occupent deux colonnes distinctes. Les réunir en une seule (« classement »)
// aurait reconduit à l'écran la confusion qu'on vient de défaire en base.
/* Les colonnes VIDES disparaissent (relevé du 27/08/2026, P6) : on coupait
   cinq titres par ellipsis pendant que « Discipline » et « Proch. revue »
   n'affichaient que des tirets sur toute la hauteur. Le calcul porte sur les
   lignes VISIBLES — un filtre qui ne rend que des procédures d'achats n'a
   aucune discipline à montrer, et la colonne revient dès qu'une ligne la
   renseigne. La largeur, elle, est désormais celle du socle. */

export function Documents() {
  const { docs, demandes, settings, fQuery, fType, fDisc, fProc, fStatut, fMoi, setFilter, openDoc, session, loading, booted, go, backendError, refresh } = useGed();
  const canWrite = session.roles.includes('redacteur') || session.roles.includes('rq');
  /* MAN-5 — RELANCE DE MASSE. La sélection est un MODE, pas une colonne
     permanente : chaque ligne du catalogue EST un bouton qui ouvre le document,
     et lui greffer une case à cocher en permanence rendrait deux gestes
     concurrents sur la même cible. Hors mode, l'écran ne bouge pas.
     ⚠️ Réservée au responsable qualité : la route l'exige (`forbidUnless
     rq`). L'afficher à un rédacteur donnerait un bouton qui refuse. */
  const estRQ = session.roles.includes('rq');
  const [modeSelection, setModeSelection] = React.useState(false);
  const [choisis, setChoisis] = React.useState<string[]>([]);
  const [relanceOuverte, setRelanceOuverte] = React.useState(false);
  const quitterSelection = () => { setModeSelection(false); setChoisis([]); };
  /* Lot GED-2 — « Nouvelle demande » MENAIT à l'écran des demandes (`go('req')`).
     On perdait ce qu'on était en train de regarder : la recherche, les filtres,
     le tri, les étiquettes cochées, la position dans la liste. Or c'est
     précisément en parcourant le fonds qu'on constate qu'un document manque : la
     demande naît ICI, elle ne doit pas coûter le contexte qui l'a fait naître.
     L'assistant s'ouvre donc PAR-DESSUS le catalogue, qui reste monté intact. */
  const [assistantOuvert, setAssistantOuvert] = React.useState(false);

  const types = settings?.docTypes?.filter((t) => t.active) || [];
  const typeLabel = (code: string) => types.find((t) => t.code === code)?.label || code;

  // Le filtre listait les seules disciplines DÉCLARÉES au paramétrage : les
  // documents classés en Biochimie, Hématologie… étaient donc impossibles à
  // filtrer. On prend l'union des disciplines déclarées et de celles réellement
  // portées par les documents, pour qu'aucune valeur affichée ne soit exclue.
  const discs = React.useMemo(() => {
    const declarees = (settings?.disciplines || []).filter((d) => d.active).map((d) => d.name);
    const portees = docs.map((d) => d.discipline).filter(Boolean) as string[];
    return [...new Set([...declarees, ...portees])].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [settings?.disciplines, docs]);

  // Même raisonnement que pour les disciplines, sur l'autre axe : un processus
  // porté par un document mais désactivé au module Processus reste filtrable,
  // sans quoi les documents qu'il classe deviendraient introuvables.
  const procs = React.useMemo(() => {
    const declares = (settings?.processus || []).filter((p) => p.active !== false).map((p) => p.name);
    const portes = docs.map((d) => d.processus).filter(Boolean) as string[];
    return [...new Set([...declares, ...portes])].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [settings?.processus, docs]);

  // Étiquettes libres (tags) : filtre local — un document doit porter TOUTES
  // les étiquettes sélectionnées (intersection), pour affiner en combinant.
  const [tagSel, setTagSel] = React.useState<string[]>([]);
  const allTags = React.useMemo(
    () => [...new Set(docs.flatMap((d) => d.tags || []))].sort((a, b) => a.localeCompare(b, 'fr')),
    [docs],
  );
  const toggleTag = (t: string) => setTagSel((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const q = fQuery.trim().toLowerCase();
  const filtres = [fQuery, fType, fDisc, fProc, fStatut, fMoi].filter(Boolean).length + tagSel.length;
  const rows = React.useMemo(() => {
    const out = docs.filter((d) => {
      if (fType && d.docType !== fType) return false;
      if (fDisc && d.discipline !== fDisc) return false;
      // Les deux axes se CROISENT : filtrer sur « Pré-analytique » et
      // « Biochimie » ensemble a un sens, et c'est même la question utile.
      if (fProc && (d.processus || '') !== fProc) return false;
      // `_circuit` regroupe les deux étapes de revue : c'est le sous-ensemble
      // que désigne la tuile « En circuit » du tableau de bord, et il n'était
      // exprimable par aucune valeur de statut isolée.
      if (fStatut === '_circuit') { if (!['averifier', 'aapprouver'].includes(d.statut)) return false; }
      else if (fStatut && d.statut !== fStatut) return false;
      if (tagSel.length && !tagSel.every((t) => (d.tags || []).includes(t))) return false;
      // « Mes documents » : la même règle que « Ma vue » (lib/mes-documents.ts).
      if (fMoi && !filtreMoi(d, session, fMoi)) return false;
      if (q) {
        const hay = `${d.code} ${d.titre} ${d.discipline} ${d.processus || ''} ${(d.motsCles || []).join(' ')} ${(d.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    /* Le TRI ne se fait plus ici : il appartient à `Tableau`, avec le
       déplacement des colonnes et la mémorisation par poste de travail. Ce que
       cette fonction rend est la liste FILTRÉE, dans l'ordre du serveur. */
    return out;
  }, [docs, fType, fDisc, fProc, fStatut, fMoi, session, tagSel, q]);

  /* Ce qui décide de la présence des deux colonnes ÉTROITES — voir `gabarit`.
     Calculé sur les lignes VISIBLES et non sur tout le fonds documentaire : un
     filtre qui ne rend que des procédures d'achats n'a aucune discipline à
     montrer, et la colonne n'a alors rien à occuper. */
  const avecDiscipline = rows.some((d) => !!d.discipline);
  const avecRevue = rows.some((d) => !!d.prochaineRevue);

  /* LES CINQ CHIFFRES DU FONDS DOCUMENTAIRE (déplacés du tableau de bord le
     04/10/2026, à la demande de la RQ). Ici, une tuile FILTRE la liste juste
     en dessous au lieu d'y mener ; les deux dernières ouvrent leur écran. */
  const filtrerStatut = (statut: string) => {
    setFilter('fQuery', ''); setFilter('fType', ''); setFilter('fDisc', ''); setFilter('fProc', ''); setFilter('fMoi', '');
    setFilter('fStatut', statut);
  };
  const tuiles = [
    { v: docs.length, l: 'Documents', aide: 'Afficher tous les documents', actif: !fStatut && !fMoi, onClick: () => filtrerStatut('') },
    { v: docs.filter((d) => d.statut === 'envigueur').length, l: 'En vigueur', aide: 'Filtrer les documents en vigueur', actif: fStatut === 'envigueur', onClick: () => filtrerStatut('envigueur') },
    { v: docs.filter((d) => ['averifier', 'aapprouver'].includes(d.statut)).length, l: 'En circuit', aide: 'Filtrer les documents en circuit de revue', actif: fStatut === '_circuit', onClick: () => filtrerStatut('_circuit') },
    { v: docs.reduce((n, d) => n + ((d.diffusion || []).filter((x) => x.requis && !x.dateAck).length), 0), l: 'Accusés en attente', aide: 'Ouvrir le suivi des accusés de lecture', actif: false, onClick: () => go('diff') },
    { v: demandes.filter((d) => !d.decision || d.decision === 'discuter').length, l: 'Demandes à statuer', aide: 'Ouvrir les demandes en attente de décision', actif: false, onClick: () => go('req') },
  ];

  /* « VOIR LES N » PROLONGE LE MENU DES TÂCHES (RQ, 06/10/2026). Quand la liste
     est filtrée « À moi d'agir — rédiger / vérifier / approuver », elle EST le
     menu du tableau de bord déplié : une fiche ouverte d'ici doit donc montrer
     la version en chantier, comme depuis « Ma vue ». Sans ce filtre — catalogue
     ordinaire, recherche — c'est la version APPLICABLE qui s'ouvre, avec la
     mention vers le chantier. */
  const etapeDuFiltre = fMoi.startsWith('amoi:') ? fMoi.slice('amoi:'.length) : '';
  const voirEnCoursDepuisFiltre = ['rediger', 'verifier', 'approuver'].includes(etapeDuFiltre);
  const ongletDArrivee = etapeDuFiltre === 'rediger' ? ('fichier' as const) : ('lecture' as const);

  const resetFiltres = () => { setFilter('fQuery', ''); setFilter('fType', ''); setFilter('fDisc', ''); setFilter('fProc', ''); setFilter('fStatut', ''); setFilter('fMoi', ''); setTagSel([]); };
  const sel: React.CSSProperties = { ...inputStyle, width: 'auto', minWidth: 150, padding: '0 10px' };
  // Tant que le premier chargement n'a pas rendu, « aucun document » serait faux.
  const enChargement = !booted || (loading && docs.length === 0);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Le catalogue reste monté derrière : au retour, mêmes filtres, même tri,
          même ligne sous les yeux. Les filtres en cours servent de PROPOSITION —
          catalogue filtré sur « Biochimie », la demande part sur la biochimie. */}
      {assistantOuvert && (
        <AssistantDemande
          contexte={{ type: fType || undefined, discipline: fDisc || undefined }}
          onFerme={() => setAssistantOuvert(false)} />
      )}
      <EnteteEcran rubrique="Documents & diffusion" clauseISO="8.3" titre="Documents"
        actions={(
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {estRQ && !modeSelection && (
              <Bouton taille="sm" onClick={() => setModeSelection(true)}>
                <I n="send" s={13} />Relancer des lectures
              </Bouton>
            )}
            {canWrite && <BoutonCreer onClick={() => setAssistantOuvert(true)}>Nouvelle demande</BoutonCreer>}
          </div>
        )} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
        {enChargement ? <SkeletonTiles n={5} /> : tuiles.map((k) => (
          <button key={k.l} onClick={k.onClick} title={k.aide} aria-pressed={k.actif} className="ged-btn"
            style={{ ...card, padding: '12px 16px', textAlign: 'start', cursor: 'pointer', font: 'inherit', fontFamily: 'var(--font-sans)',
              // Une clé à `undefined` ÉCRASERAIT le fond de la carte (bouton gris) : on ne l'ajoute que si la tuile est active.
              ...(k.actif ? { borderColor: 'var(--cyan-500)', background: 'var(--cyan-50)' } : {}) }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600, color: 'var(--navy-900)', lineHeight: 1 }}>{k.v}</div>
            <div style={{ fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 6 }}>{k.l}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <ChampRecherche valeur={fQuery} onChange={(v) => setFilter('fQuery', v)} placeholder="Rechercher (code, titre, mot-clé…)" style={{ flex: '1 1 220px' }} />
        <select value={fType} onChange={(e) => setFilter('fType', e.target.value)} aria-label="Filtrer par type" style={sel}>
          <option value="">Tous types</option>
          {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
        <Selecteur value={fProc} onChange={(v) => setFilter('fProc', v)} aria-label="Filtrer par processus" style={sel}>
          <option value="">Tous processus</option>
          {procs.map((p) => <option key={p} value={p}>{p}</option>)}
        </Selecteur>
        <Selecteur value={fDisc} onChange={(v) => setFilter('fDisc', v)} aria-label="Filtrer par discipline (famille biologique)" style={sel}>
          <option value="">Toutes disciplines</option>
          {discs.map((d) => <option key={d} value={d}>{d}</option>)}
        </Selecteur>
        <select value={fMoi} onChange={(e) => setFilter('fMoi', e.target.value)} aria-label="Mes documents" style={sel}>
          <option value="">Tous les documents</option>
          <option value="amoi">À moi d’agir</option>
          {ETAPES_MOI.map((e) => <option key={e.code} value={`amoi:${e.code}`}>À moi d’agir — {e.label}</option>)}
          <option value="implique">M’impliquant</option>
        </select>
        <select value={fStatut} onChange={(e) => setFilter('fStatut', e.target.value)} aria-label="Filtrer par statut" style={sel}>
          <option value="">Tous statuts</option>
          <option value="_circuit">En circuit de revue</option>
          {Object.entries(STATUT_LABEL).map(([s, l]) => <option key={s} value={s}>{l}</option>)}
        </select>
      </div>

      {allTags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: -6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginInlineEnd: 4 }}>Étiquettes</span>
          {allTags.map((t) => {
            const on = tagSel.includes(t);
            return (
              <button key={t} onClick={() => toggleTag(t)} className="ged-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '4px 11px', cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--cyan-500)' : 'var(--border-default)'}`, background: on ? 'var(--cyan-50)' : 'var(--surface-card)', color: on ? 'var(--cyan-700)' : 'var(--text-body)' }}>
                {on && <I n="check" s={11} sw={2.6} />}{t}
              </button>
            );
          })}
        </div>
      )}

      {/* Compteur et remise à zéro AU-DESSUS de la liste : les filtres vivent
          dans le store et survivent à la navigation — en bas de page, le
          compteur ne prévenait pas qu'une vue partielle était affichée. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: -8 }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {enChargement ? 'Chargement…' : `${rows.length} sur ${docs.length} document(s)`}
        </span>
        {filtres > 0 && (
          <Bouton taille="sm" onClick={resetFiltres}>
            <I n="x" s={12} />Effacer les filtres ({filtres})
          </Bouton>
        )}
      </div>

      {/* ⚠️ LA BARRE DIT SUR QUOI PORTE LA SÉLECTION. « Tout sélectionner »
          prend les documents VISIBLES, c'est-à-dire filtrés — jamais le fonds
          entier. Sans le dire, quelqu'un qui a filtré sur « Biochimie » croirait
          sélectionner la biochimie et prendrait les 300 documents. */}
      {modeSelection && (
        <div style={{ ...card, padding: '11px 16px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', borderColor: 'var(--cyan-500)' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>
            {choisis.length === 0 ? 'Aucun document sélectionné' : `${choisis.length} document(s) sélectionné(s)`}
          </span>
          <Bouton taille="sm" onClick={() => setChoisis(rows.map((d) => d.documentId))}>
            Sélectionner les {rows.length} affiché(s)
          </Bouton>
          {choisis.length > 0 && (
            <Bouton taille="sm" onClick={() => setChoisis([])}>Vider la sélection</Bouton>
          )}
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" onClick={quitterSelection}>Quitter</Bouton>
          <Bouton variante="primaire" taille="sm" disabled={!choisis.length} onClick={() => setRelanceOuverte(true)}>
            <I n="send" s={13} />Relancer les lectures…
          </Bouton>
        </div>
      )}

      {relanceOuverte && (
        <RelanceLot ids={choisis} onFerme={(envoye) => { setRelanceOuverte(false); if (envoye) quitterSelection(); }} />
      )}

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {/* Migré d'une grille de `<div>` vers le tableau du socle (28/08/2026).
            Le tri existait déjà (`SortableTh` + un état local) ; il vit désormais
            dans le composant, avec le déplacement des colonnes, l'en-tête collant
            et le plancher de hauteur de ligne. Une mécanique de tri en moins à
            entretenir, et la dernière colonne d'écart avec les autres registres.
            L3 (10/10/2026) : niveau AVANCÉ — « Code » épinglé et obligatoire,
            filtres par colonne, export de référence, états intégrés (squelette,
            erreur + Réessayer, vide) rendus par le socle lui-même. */}
        <Tableau
          id="documents"
          ajusterLargeur
          niveau="avance"
          lignes={rows}
          cleLigne={(d) => d.documentId}
          parPage={PAR_PAGE}
          quoi="documents"
          chargement={enChargement}
          erreur={backendError ? 'Le fonds documentaire n’a pas pu être lu.' : null}
          onReessayer={() => { void refresh(); }}
          vide={{
            titre: docs.length === 0 ? 'Aucun document dans le fonds documentaire.' : 'Aucun document ne correspond à ces critères.',
            indice: docs.length === 0 ? 'Les documents naissent d’une demande approuvée par le responsable qualité.' : undefined,
            action: filtres > 0
              ? <Bouton taille="sm" onClick={resetFiltres}>Effacer les filtres</Bouton>
              : (canWrite && docs.length === 0 ? <Bouton variante="primaire" taille="sm" onClick={() => setAssistantOuvert(true)}><I n="plus" s={13} />Nouvelle demande</Bouton> : undefined),
          }}
          /* L'export de RÉFÉRENCE : colonnes `export: true`, lignes filtrées par
             l'écran (recherche, type, discipline, processus, statut, étiquettes)
             PUIS par le socle, l'en-tête dit lesquels. */
          export={{
            registre: 'documents', libelleRegistre: 'Fonds documentaire', auteur: session.name || session.key,
            filtres: [
              fQuery ? `Recherche « ${fQuery} »` : '', fType ? `Type : ${fType}` : '', fDisc ? `Discipline : ${fDisc}` : '',
              fProc ? `Processus : ${fProc}` : '', fStatut ? `Statut : ${stateMeta(fStatut).label}` : '', fMoi ? 'Mes documents' : '',
              ...tagSel.map((t) => `Étiquette : ${t}`),
            ].filter(Boolean),
          }}
          /* ⚠️ EN MODE SÉLECTION, LA LIGNE COCHE — ELLE N'OUVRE PLUS.
             Deux gestes sur la même cible se disputeraient le clic : on
             cliquerait pour cocher et le document s'ouvrirait par-dessus. Le
             mode est explicite (une barre le dit), il est donc licite qu'il
             change ce que fait la ligne. */
          ligneProps={(d) => ligneOuvrable(
            () => (modeSelection
              ? setChoisis((c) => (c.includes(d.documentId) ? c.filter((x) => x !== d.documentId) : [...c, d.documentId]))
              : openDoc(d.code, ongletDArrivee, voirEnCoursDepuisFiltre)),
            modeSelection ? `Cocher ${d.code}` : `Ouvrir ${d.code}`,
            modeSelection && choisis.includes(d.documentId) ? { background: 'var(--cyan-50)' } : undefined,
          )}
          colonnes={[
            ...(modeSelection ? [{
              cle: 'coche', libelle: '', style: { width: 26 },
              rendu: (d: (typeof rows)[number]) => (
                /* `aria-pressed` est porté par la LIGNE (`ligneOuvrable`) ; ici
                   la case n'est qu'un témoin, d'où `aria-hidden`. */
                <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 17, height: 17, borderRadius: 'var(--radius-xs)', border: `1.5px solid ${choisis.includes(d.documentId) ? 'var(--cyan-600)' : 'var(--border-default)'}`, background: choisis.includes(d.documentId) ? 'var(--cyan-600)' : 'var(--surface-card)', color: 'var(--text-on-primary)' }}>
                  {choisis.includes(d.documentId) && <I n="check" s={11} sw={3} />}
                </span>
              ),
            }] : []),
            {
              cle: 'code', libelle: 'Code', valeur: (d) => d.code || '',
              epinglee: true, obligatoire: true, export: true,
              style: { fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)', width: 152 },
              /* ── LA COULEUR DU TYPE, ENFIN VISIBLE (RQ, 07/09/2026) ────────
                 « Le code couleur que tu donnes aux types de documents, je ne
                 le retrouve pas après dans le document. »  Elle se réglait au
                 paramétrage et ne servait NULLE PART ailleurs : un réglage sans
                 effet, qu'on finit par croire cassé.

                 Une barre plutôt qu'une pastille : elle se lit en balayant la
                 colonne du regard, sans rien lire. ⚠️ Elle ne porte AUCUNE
                 information à elle seule — le libellé du type reste dans son
                 infobulle et dans la fiche : une couleur n'est pas lisible par
                 tout le monde, et douze pour cent des hommes ne distinguent pas
                 le rouge du vert. */
              rendu: (d) => {
                const t = (settings?.docTypes || []).find((x) => x.code === d.docType);
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span aria-hidden title={t?.label || d.docType}
                      style={{ flex: 'none', width: 3, alignSelf: 'stretch', minHeight: 15, borderRadius: 'var(--radius-xs)', background: t?.color || 'var(--border-default)' }} />
                    {d.code}
                    <IconeApercu doc={d} />
                  </span>
                );
              },
            },
            {
              cle: 'titre', libelle: 'Titre', valeur: (d) => d.titre || '', export: true,
              style: { whiteSpace: 'normal', minWidth: 220 },
              rendu: (d) => (
                <>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {/* ⚠️ LE TITRE S'ENROULE, il ne se coupe plus (Inès, 28/08).
                        Il portait `nowrap` + points de suspension, hérité de la
                        grille de `<div>` où la colonne avait une largeur fixe
                        qui le bornait. Dans un tableau en disposition
                        automatique, une cellule `nowrap` impose sa largeur
                        INTRINSÈQUE : le tableau dépassait alors le cadre, et
                        c'est la DERNIÈRE colonne qui se retrouvait tronquée —
                        le titre entier était lisible, le statut ne l'était plus.
                        Enroulé, il rend au contraire la largeur aux autres.
                        C'est le partage voulu : le titre sur deux lignes, pas
                        le code ni le statut. */}
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>{d.titre}</span>
                    {/* Là où je suis destinataire requis : pastille de MA lecture —
                        verte « Lu » une fois l'accusé donné, ambre « À lire » sinon. */}
                    {(() => {
                      const l = monEtatLecture(d, session.key);
                      if (!l) return null;
                      return l.etat === 'lu'
                        ? <span title={`Lu le ${fmtDate(l.dateAck)}`} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--green-50)', color: 'var(--green-600)' }}><I n="check" s={11} sw={2.6} />Lu</span>
                        : <span title="Accusé de lecture à donner" style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>À lire</span>;
                    })()}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{typeLabel(d.docType)}{d.classe === 'importe' ? ' · importé' : ''}</span>
                    {(d.tags || []).map((t) => (
                      <span key={t} style={{ fontSize: 10, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '1px 7px', background: 'var(--cyan-50)', color: 'var(--cyan-700)' }}>{t}</span>
                    ))}
                  </span>
                </>
              ),
            },
            {
              /* Un axe vide se DIT, il ne se laisse pas blanc : une case vide se
                 lit « non renseigné » alors qu'ici elle veut souvent dire « ne
                 relève d'aucune famille biologique », ce qui est un fait et non
                 un oubli — une procédure d'achats n'a pas de discipline. */
              cle: 'processus', libelle: 'Processus', valeur: (d) => d.processus || '', type: 'liste' as const, export: true,
              style: { fontSize: 12.5, width: 126 },
              rendu: (d) => <span style={{ color: d.processus ? 'var(--text-body)' : 'var(--text-muted)' }}>{d.processus || '—'}</span>,
            },
            ...(avecDiscipline ? [{
              cle: 'discipline', libelle: 'Discipline', valeur: (d: (typeof rows)[number]) => d.discipline || '', type: 'liste' as const, export: true,
              style: { fontSize: 12.5, width: 116 },
              rendu: (d: (typeof rows)[number]) => <span style={{ color: d.discipline ? 'var(--text-body)' : 'var(--text-muted)' }}>{d.discipline || '—'}</span>,
            }] : []),
            {
              cle: 'version', libelle: 'Ver.', valeur: (d) => d.versionCourante, type: 'nombre' as const, export: true,
              exporter: (d) => `v${d.versionCourante}`,
              style: { fontFamily: 'var(--font-mono)', fontSize: 12.5, width: 56 },
              rendu: (d) => `v${d.versionCourante}`,
            },
            ...(avecRevue ? [{
              cle: 'revue', libelle: 'Proch. revue',
              // La date BRUTE, et les vides en queue quel que soit le sens :
              // trier par échéance doit montrer les échéances.
              valeur: (d: (typeof rows)[number]) => d.prochaineRevue || '',
              type: 'date' as const, export: true, exporter: (d: (typeof rows)[number]) => (d.prochaineRevue ? fmtDate(d.prochaineRevue) : ''),
              style: { width: 104 },
              rendu: (d: (typeof rows)[number]) => {
                const jours = d.statut === 'envigueur' || d.statut === 'enrevision' ? joursRestants(d.prochaineRevue) : null;
                return (
                  <>
                    <span style={{ display: 'block', fontSize: 12.5, color: jours !== null && jours < 0 ? 'var(--red-600)' : 'var(--text-muted)', fontWeight: jours !== null && jours < 0 ? 700 : 400 }}>
                      {d.prochaineRevue ? fmtDate(d.prochaineRevue) : '—'}
                    </span>
                    <LateBadge jours={jours} libelleRetard="Revue échue" seuilAmbre={30} />
                  </>
                );
              },
            }] : []),
            {
              cle: 'statut', libelle: 'Statut', valeur: (d) => d.statut,
              type: 'liste' as const, libelleValeur: (v: string) => stateMeta(v).label, export: true, exporter: (d) => stateMeta(d.statut).label,
              style: { width: 118 },
              rendu: (d) => <StatePill statut={d.statut} />,
            },
          ]}
        />
      </div>
    </div>
  );
}
